import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert, TextField, Link, Dialog, DialogContent, DialogTitle, Card, Button, Table, Stack,
  Paper, Avatar, Popover, TableRow, MenuItem, TableBody, TableCell, Container, Typography,
  IconButton, TableContainer, TablePagination, ToggleButton, ToggleButtonGroup, Checkbox, Box, CircularProgress, Snackbar
} from '@mui/material';
import api from 'src/utils/api';
import profile from 'src/store/profile';
import moment from 'moment';
import 'moment/locale/uk';

import { CreateReport } from 'src/sections/@dashboard/student';
import { UserListHead, UserListToolbar } from '../../sections/@dashboard/user';
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';

moment().locale('uk');

const TABLE_HEAD = [
  { id: 'name', label: "Ім'я", alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'phone', label: 'Телефон', alignRight: false },
  { id: 'status', label: 'Статус', alignRight: false },
  { id: '' },
];

function applySortFilter(array, comparator, query) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    return order !== 0 ? order : a[1] - b[1];
  });
  if (query) return filter(array, (_u) => (_u.name || '').toLowerCase().includes(query.toLowerCase()));
  return stabilized.map((el) => el[0]);
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function UserPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const groupId = location.state?.id;
  const isSubstitution = location.state?.is_substitution;
  const defaultDate = location.state?.substitution_date || new Date().toISOString().split('T')[0];

  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState([]); 
  const [monthData, setMonthData] = useState({}); 
  const [groupDays, setGroupDays] = useState({ first_day: null, second_day: null });
  
  const [isDayLoading, setIsDayLoading] = useState(false);
  const [isMonthLoading, setIsMonthLoading] = useState(false);
  const [isScheduled, setIsScheduled] = useState(true);
  const [substitutedTeacherId, setSubstitutedTeacherId] = useState(null);
  
  const [isOpen, setIsOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [tab, setTab] = useState('students');
  const [activeDate, setActiveDate] = useState(defaultDate);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchDayData = useCallback(() => {
    if (!groupId || !activeDate) return;
    setIsDayLoading(true);
    setIsScheduled(true); 
    setSubstitutedTeacherId(null);
    
    api.get(`attendance-api/day-list-v2?group_id=${groupId}&date=${encodeURIComponent(activeDate)}`)
      .then((res) => {
        if (!res || res.ok === false) return;
        setIsScheduled(res.is_scheduled !== false);
        setSubstitutedTeacherId(res.substituted_teacher_id || null);

        const presentIds = [];
        const list = (res.items || []).map((it) => {
          const status = it.status !== null ? Number(it.status) : null;
          const id = Number(it.student_id);
          
          if (status === 1 || status === 3) presentIds.push(id);
          
          return {
            id,
            name: it.name || `ID ${id}`,
            email: it.email || '',
            phone: it.phone || '',
            isGuest: Number(it.is_guest) === 1,
            status, 
            is_scheduled_for_day: Number(it.is_scheduled_for_day) === 1,
            username: it.username || '',
            studio_id: it.studio_id ? Number(it.studio_id) : null,
            sgroup_id: it.sgroup_id ? Number(it.sgroup_id) : null,
            sday: it.sday !== null ? Number(it.sday) : null,
            tgroup_id: it.tgroup_id ? Number(it.tgroup_id) : null,
            tday: it.tday !== null ? Number(it.tday) : null,
            info: it.info || '',
            birthday: it.birthday || null,
            path: it.path || '',
            selfstudy: it.selfstudy !== null ? Number(it.selfstudy) : 0,
            course_id: it.course_id ? Number(it.course_id) : null,
          };
        });
        
        setStudents(list);
        setSelected(presentIds);
      })
      .catch(() => {
        setStudents([]);
        setSelected([]);
      })
      .finally(() => setIsDayLoading(false));
  }, [groupId, activeDate]);

  const fetchMonthData = useCallback(() => {
    if (!groupId || !activeDate) return;
    setIsMonthLoading(true);
    const month = moment(activeDate).format('YYYY-MM');
    
    api.get(`attendance-api/month-grid-v2?group_id=${groupId}&month=${month}`)
      .then((res) => {
        if (!res || res.ok === false) return;
        
        if (res.group_days) {
          setGroupDays({
            first_day: res.group_days.first_day ? Number(res.group_days.first_day) : null,
            second_day: res.group_days.second_day ? Number(res.group_days.second_day) : null
          });
        }
        
        const map = {};
        (res.items || []).forEach(record => {
          map[`${record.student_id}_${record.date}`] = Number(record.status);
        });
        setMonthData(map);
      })
      .catch(() => {
        setMonthData({});
        setGroupDays({ first_day: null, second_day: null });
      })
      .finally(() => setIsMonthLoading(false));
  }, [groupId, activeDate]);

  useEffect(() => {
    fetchDayData();
    fetchMonthData();
  }, [fetchDayData, fetchMonthData]);

  const dayStudents = students.filter(s => s.is_scheduled_for_day || s.isGuest);

  const handleSave = () => {
    setIsDayLoading(true);
    setIsMonthLoading(true);

    const attendancePayload = dayStudents.map((student) => {
      const isPresent = selected.includes(student.id);
      return {
        student_id: student.id,
        status: isPresent ? 1 : 0
      };
    });
    
    api.post(`attendance-api/day-sync-v2?group_id=${groupId}&date=${encodeURIComponent(activeDate)}`, { 
      attendance: attendancePayload,
      teacher_id: profile?.data?.id
    })
      .then((res) => {
        if (!res || res.ok === false) {
          api.logError('attendance save failed', res?.error);
          setSnackbar({ open: true, message: res?.error || 'Помилка при збереженні відвідуваності', severity: 'error' });
          setIsDayLoading(false);
          setIsMonthLoading(false);
          return;
        }
        setIsEdit(false);
        setTab('grid'); 
        setSnackbar({ open: true, message: 'Відвідуваність збережена', severity: 'success' });

        fetchDayData();
        fetchMonthData();
      })
      .catch((err) => {
        api.logError('attendance save failed', err);
        setSnackbar({ open: true, message: err?.message || 'Помилка при збереженні відвідуваності', severity: 'error' });
        setIsDayLoading(false);
        setIsMonthLoading(false);
      });
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) setSelected(dayStudents.map((n) => n.id));
    else setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) newSelected = newSelected.concat(selected, id);
    else if (selectedIndex === 0) newSelected = newSelected.concat(selected.slice(1));
    else if (selectedIndex === selected.length - 1) newSelected = newSelected.concat(selected.slice(0, -1));
    else newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    setSelected(newSelected);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredUsers = applySortFilter(dayStudents, getComparator(order, orderBy), filterName);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dayStudents.length) : 0;
  const isNotFound = !filteredUsers.length && !!filterName;

  const daysInMonth = moment(activeDate).daysInMonth();
  const monthDaysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Допоміжна змінна для перевірки доступу
  const isSubstituteMismatch = substitutedTeacherId && Number(substitutedTeacherId) !== Number(profile?.data?.id);

  return (
    <>
      <Helmet><title>Студенти | Lala English</title></Helmet>

      <Alert severity="info" sx={{ mb: 5, pl: 3 }}>Керування студентами та відвідуваністю групи.</Alert>
      
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Button onClick={() => navigate(-1)} variant="outlined" startIcon={<Iconify icon="eva:arrow-back-fill" />} sx={{ textTransform: 'none' }}>
            Назад
          </Button>
          <Typography variant="h4" sx={{ px: 2, py: 0.5, borderRadius: 1, backgroundColor: 'rgba(255,255,255,0.9)', color: 'text.secondary' }}>
            Група "{location.state?.name || '-'}" {isSubstitution && '(Заміна)'}
          </Typography>
          
          {isEdit && tab === 'students' ? (
            <Button onClick={handleSave} variant="contained" startIcon={<Iconify icon="eva:save-fill" />} sx={{ textTransform: 'none' }} disabled={isDayLoading || !isScheduled || isSubstituteMismatch}>
              Зберегти відвідуваність
            </Button>
          ) : (
            <Button onClick={() => { setTab('students'); setIsEdit(true); }} variant="contained" startIcon={<Iconify icon="eva:edit-2-fill" />} sx={{ textTransform: 'none' }} disabled={isDayLoading || !isScheduled || isSubstituteMismatch}>
              Внести відвідуваність
            </Button>
          )}

          <Dialog onClose={() => setIsOpen(false)} open={isOpen}>
            <DialogTitle><Typography>Звіт про урок</Typography></DialogTitle>
            <DialogContent><CreateReport success={() => setIsOpen(false)} /></DialogContent>
          </Dialog>
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <ToggleButtonGroup value={tab} exclusive onChange={(_, v) => v && setTab(v)}>
            <ToggleButton value="students" sx={{ textTransform: 'none', px: 3 }}>Студенти</ToggleButton>
            <ToggleButton value="grid" sx={{ textTransform: 'none', px: 3 }}>Журнал</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            type="date"
            value={activeDate}
            disabled={isSubstitution}
            onChange={(e) => {
              const today = new Date().toISOString().split('T')[0];
              setActiveDate(e.target.value > today ? today : e.target.value);
            }}
            inputProps={{ max: new Date().toISOString().split('T')[0] }}
            size="small"
            sx={{ '& .MuiInputBase-root': { borderRadius: 1 } }}
          />
        </Stack>

        {tab === 'students' ? (
          <Card>
            <Box sx={{ position: 'relative' }}>
              {!isScheduled ? (
                <Box sx={{ py: 10, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">На цю дату немає занять</Typography>
                </Box>
              ) : isSubstituteMismatch ? (
                <Box sx={{ py: 10, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">На цю дату призначений вчитель на заміну</Typography>
                </Box>
              ) : (
                <>
                  <UserListToolbar numSelected={selected.length} filterName={filterName} onFilterName={setFilterName} isEdit={isEdit} onBack={() => setIsEdit(false)} />
                  <Scrollbar>
                    <TableContainer sx={{ minWidth: 800 }}>
                      <Table>
                        <UserListHead
                          order={order}
                          orderBy={orderBy}
                          headLabel={TABLE_HEAD}
                          rowCount={dayStudents.length}
                          numSelected={selected.length}
                          onRequestSort={handleRequestSort}
                          onSelectAllClick={isEdit ? handleSelectAllClick : undefined}
                        />
                        <TableBody>
                          {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                            const { id, name, phone, email, status, isGuest } = row;
                            const selectedUser = selected.indexOf(id) !== -1;

                            return (
                              <TableRow hover key={id} tabIndex={-1} selected={selectedUser}>
                                <TableCell padding="checkbox">
                                  {isEdit && <Checkbox checked={selectedUser} onChange={(e) => handleClick(e, id)} />}
                                </TableCell>
                                <TableCell component="th" scope="row" padding="normal">
                                  <Stack direction="row" alignItems="center" spacing={2}>
                                    <Avatar alt={name} src="/assets/images/avatars/avatar_2.jpg" />
                                    <Box>
                                      <Link component="button" variant="subtitle2" color="text.primary" onClick={() => navigate('/teacher/profile', { state: row })}>{name}</Link>
                                      {isGuest && <Typography variant="caption" sx={{ ml: 1, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'warning.lighter', color: 'warning.darker' }}>Гість</Typography>}
                                    </Box>
                                  </Stack>
                                </TableCell>
                                <TableCell align="left">{email}</TableCell>
                                <TableCell align="left">{phone}</TableCell>
                                <TableCell align="left">
                                  <Typography variant="body2" color={status === 0 ? 'error' : status === 1 || status === 3 ? 'success.main' : 'text.secondary'}>
                                    {status === null ? 'Не внесено' : status === 0 ? 'Відсутній' : status === 1 ? 'Присутній' : status === 2 ? 'Заплановано' : 'Відпрацьовано'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton size="large" color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}><Iconify icon="eva:more-vertical-fill" /></IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {emptyRows > 0 && <TableRow style={{ height: 53 * emptyRows }}><TableCell colSpan={6} /></TableRow>}
                        </TableBody>
                        {isNotFound && (
                          <TableBody>
                            <TableRow>
                              <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                                <Paper sx={{ textAlign: 'center', boxShadow: 'none' }}>
                                  <Typography variant="h6" paragraph>Не знайдено</Typography>
                                  <Typography variant="body2">Немає результатів для <strong>&quot;{filterName}&quot;</strong>.</Typography>
                                </Paper>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        )}
                      </Table>
                    </TableContainer>
                  </Scrollbar>
                  
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={dayStudents.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, np) => setPage(np)}
                    onRowsPerPageChange={(e) => { setPage(0); setRowsPerPage(parseInt(e.target.value, 10)); }}
                  />
                </>
              )}
              
              {isDayLoading && (
                <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, bgcolor: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>
          </Card>
        ) : (
          <Card>
            <Box sx={{ position: 'relative' }}>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 1000, maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ minWidth: 200, fontWeight: 'bold', borderRight: '1px solid #eee' }}>Студент</TableCell>
                        {monthDaysArray.map(day => {
                          const dateStr = moment(activeDate).date(day).format('YYYY-MM-DD');
                          const dow = moment(dateStr).isoWeekday();
                          const isGroupDay = (groupDays.first_day === dow || groupDays.second_day === dow);
                          
                          return (
                            <TableCell key={day} align="center" sx={{ minWidth: 40, p: 0.5, fontWeight: 'bold', bgcolor: isGroupDay ? '#fff7cd' : 'transparent' }}>
                              {day}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                      
                      {students.map((student) => (
                        <TableRow hover key={student.id}>
                          <TableCell sx={{ borderRight: '1px solid #eee' }}>
                            <Typography variant="body2" noWrap>{student.name} {student.isGuest && '(Гість)'}</Typography>
                          </TableCell>
                          {monthDaysArray.map(day => {
                            const dateStr = moment(activeDate).date(day).format('YYYY-MM-DD');
                            const dow = moment(dateStr).isoWeekday();
                            
                            let dayType = 0;
                            if (groupDays.first_day === dow) dayType = 1;
                            else if (groupDays.second_day === dow) dayType = 2;
                            
                            const isGroupDay = (dayType > 0);
                            
                            let isScheduledForCell = false;
                            if (!student.isGuest && isGroupDay) {
                              const checkGroup = (gId, dayVal) => {
                                if (gId !== groupId) return false;
                                if (!dayVal || dayVal === 0) return true;
                                return dayVal === dayType;
                              };
                              isScheduledForCell = checkGroup(student.sgroup_id, student.sday) || checkGroup(student.tgroup_id, student.tday);
                            }

                            const cellStatus = monthData[`${student.id}_${dateStr}`];
                            let cellContent = '-'; 
                            let cellColor = 'transparent'; 
                            let textColor = 'text.secondary';

                            if (cellStatus === 0) { cellContent = 'Н'; cellColor = '#ffe7d9'; textColor = '#b72136'; }
                            else if (cellStatus === 1) { cellContent = 'П'; cellColor = '#c8facd'; textColor = '#007b55'; }
                            else if (cellStatus === 2) { cellContent = 'З'; cellColor = '#fff7cd'; textColor = '#b78103'; }
                            else if (cellStatus === 3) { cellContent = 'В'; cellColor = '#d0f2ff'; textColor = '#04297a'; }

                            if (isGroupDay && !isScheduledForCell && cellStatus === undefined) {
                              cellColor = '#f4f6f8';
                            }

                            return (
                              <TableCell key={day} align="center" sx={{ p: 0, borderRight: '1px solid #f5f5f5' }}>
                                <Box sx={{ width: '100%', height: '100%', minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: cellColor, color: textColor, fontWeight: 'bold' }}>
                                  {cellContent !== '-' && cellContent}
                                </Box>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              {isMonthLoading && (
                <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, bgcolor: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>
          </Card>
        )}
      </Container>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 1, width: 140 } }}
      >
        <MenuItem onClick={() => setAnchorEl(null)}><Iconify icon="eva:edit-fill" sx={{ mr: 2 }} />Редагувати</MenuItem>
        <MenuItem onClick={() => setAnchorEl(null)} sx={{ color: 'error.main' }}><Iconify icon="eva:trash-2-outline" sx={{ mr: 2 }} />Видалити</MenuItem>
      </Popover>
    </>
  );
}